/**
 * LoginPage — Portal email + password login.
 *
 * Public route. After a successful login, redirects to the location the user
 * was trying to reach (state.from from RequireAuth) or /users as the default.
 *
 * Shows a success banner when:
 *   ?activated=1 — user just accepted an invitation (BOLT-022)
 *   ?reset=1     — user just completed a password reset (BOLT-019)
 */
import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link as RouterLink } from 'react-router';
import { Alert, Box, Button, Divider, InputAdornment, Link, Stack, TextField } from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { normalizedEmailSchema } from '@crm98/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiClient, ApiError } from '@/lib/api-client.js';
import { useAuthStore } from '@/store/auth.js';
import type { LoginResponse } from '@crm98/shared';
import type { UserRole } from '@/store/auth.js';
import { filledInputSx, outerBoxSx, BrandMark } from './auth-shared.js';

const loginSchema = z.object({
  email: normalizedEmailSchema,
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [orgSuspended, setOrgSuspended] = useState(false);

  const activated = searchParams.get('activated') === '1';
  const passwordReset = searchParams.get('reset') === '1';

  // Preserve the full intended URL (path + search + hash) so post-login redirects
  // re-fire query-param-driven UX like the email-verification toast on /account-settings.
  const fromLocation = (location.state as { from?: Location })?.from;
  const from = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search ?? ''}${fromLocation.hash ?? ''}`
    : '/';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = handleSubmit(async (data) => {
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await apiClient.post<LoginResponse>('/v1/auth/login', {
        body: { email: data.email, password: data.password },
        // Prevent the apiClient from attempting silentRefresh on a 401 here.
        // Without this, a wrong-credentials 401 triggers a token refresh attempt
        // which fails and calls window.location.replace('/login'), wiping the form.
        skipRefresh: true,
      });
      setAuth(res.access_token, res.user.org_id ?? '', res.user.id, res.user.role as UserRole);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'org_suspended') {
          setOrgSuspended(true);
          return;
        } else if (err.status === 401) {
          setServerError('Invalid email or password.');
        } else if (err.status === 403) {
          setServerError('Your account has been disabled. Contact your administrator.');
        } else if (err.status === 429) {
          setServerError('Too many login attempts. Please wait a moment and try again.');
        } else {
          setServerError('Something went wrong. Please try again.');
        }
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  });

  if (orgSuspended) {
    return (
      <Box sx={outerBoxSx}>
        <Alert severity="warning" sx={{ maxWidth: 440 }}>
          Your organization has been suspended. Please contact support for assistance.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={outerBoxSx}>
      <BrandMark />

      <Box sx={{ width: '100%', maxWidth: 440 }}>
        {activated && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Account activated. Please log in.
          </Alert>
        )}

        {passwordReset && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Password reset successfully. Please log in.
          </Alert>
        )}

        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}

        <form onSubmit={onSubmit}>
          <Stack spacing={1.5}>
            <TextField
              hiddenLabel
              placeholder="Email"
              type="email"
              autoComplete="email"
              variant="filled"
              {...register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              disabled={submitting}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
              }}
              sx={filledInputSx}
            />

            <TextField
              hiddenLabel
              placeholder="Password"
              type="password"
              autoComplete="current-password"
              variant="filled"
              {...register('password')}
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              disabled={submitting}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
              }}
              sx={filledInputSx}
            />
          </Stack>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, borderRadius: 8, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
          >
            Sign In
          </Button>
        </form>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Link component={RouterLink} to="/forgot-password" variant="body2">
            Forgot password?
          </Link>
        </Box>
      </Box>
    </Box>
  );
}
